/**
 * Bluedot WebView Integration with Native Geolocation Patch
 *
 * Handles:
 * - Permission detection (native + browser)
 * - Geolocation patching for WebView
 * - Integration with native app geolocation
 * - Bluedot SDK initialization optimization
 */

import { bluedot } from "@bluedot-innovation/javascript-sdk";
import bluedotConfig from "../config/bluedot";
import { isWebview, isNativeApp } from "./isWebview";

// Module state
let isInitialized = false;
let initializationError = null;
let locationEnabled = false;
let locationPermissionGranted = false;
let nativePermissionChecked = false;
let initializationPromise = null;

// Native permission state
let nativeLocationPermission = "unknown"; // "granted" | "denied" | "unknown"

/**
 * Check browser location permission status
 *
 * @returns {Promise<string>} - "granted" | "denied" | "prompt" | "unsupported"
 */
export const checkBrowserPermission = async () => {
  if (typeof navigator === "undefined" || !navigator.permissions) {
    return "unsupported";
  }

  try {
    const result = await navigator.permissions.query({ name: "geolocation" });
    return result.state; // "granted" | "denied" | "prompt"
  } catch (error) {
    console.warn(
      "[Bluedot-WebView] Browser permission API not supported:",
      error.message
    );
    return "unsupported";
  }
};

/**
 * Request location permission from native app (WebView)
 *
 * @returns {Promise<{granted: boolean, position?: Object, error?: string}>}
 */
export const requestNativeLocationPermission = async () => {
  return new Promise(resolve => {
    if (!isWebview()) {
      resolve({ granted: false, error: "Not in WebView" });
      return;
    }

    // Set up handlers before requesting
    window.onNativeLocationPermissionResponse = response => {
      try {
        const parsed =
          typeof response === "string" ? JSON.parse(response) : response;
        if (parsed.granted) {
          resolve({ granted: true, position: parsed.position });
        } else {
          resolve({
            granted: false,
            error: parsed.error || "Permission denied"
          });
        }
      } catch (error) {
        resolve({ granted: false, error: error.message });
      }
    };

    // Send request to native app
    const requestId = `permission_${Date.now()}`;
    window.pendingNativePermissionRequest = requestId;

    window.postMessage(
      JSON.stringify({
        type: "REQUEST_LOCATION_PERMISSION",
        requestId
      }),
      "*"
    );

    // Timeout after 10 seconds
    setTimeout(() => {
      if (window.pendingNativePermissionRequest === requestId) {
        delete window.pendingNativePermissionRequest;
        resolve({ granted: false, error: "Native permission request timeout" });
      }
    }, 10000);
  });
};

/**
 * Check if native app has already granted location permission
 * Falls back to browser permission check if not in WebView
 *
 * @returns {Promise<boolean>}
 */
export const checkNativeLocationPermission = async () => {
  // Always check browser permission first (works in all environments)
  const browserStatus = await checkBrowserPermission();
  if (browserStatus === "granted") {
    nativeLocationPermission = "browser_granted";
    locationPermissionGranted = true;
    nativePermissionChecked = true;
    return true;
  }

  // If in WebView, also check native permission
  if (isWebview()) {
    if (nativePermissionChecked) {
      return locationPermissionGranted;
    }

    return new Promise(resolve => {
      const requestId = `check_${Date.now()}`;
      window.onNativePermissionCheckResponse = response => {
        try {
          const parsed =
            typeof response === "string" ? JSON.parse(response) : response;
          nativeLocationPermission = parsed.granted
            ? "native_granted"
            : "denied";
          locationPermissionGranted = parsed.granted;
          nativePermissionChecked = true;
          resolve(parsed.granted);
        } catch (error) {
          nativeLocationPermission = "error";
          resolve(false);
        }
      };

      window.postMessage(
        JSON.stringify({
          type: "CHECK_LOCATION_PERMISSION",
          requestId
        }),
        "*"
      );

      // Timeout after 5 seconds
      setTimeout(() => {
        if (!nativePermissionChecked) {
          nativePermissionChecked = true;
          nativeLocationPermission = "timeout";
          resolve(false);
        }
      }, 5000);
    });
  }

  // Not in WebView and browser permission not granted
  nativeLocationPermission =
    browserStatus === "denied" ? "browser_denied" : "browser_prompt";
  nativePermissionChecked = true;
  return false;
};

/**
 * Patch navigator.geolocation for WebView to use native geolocation
 * This integrates with the React Native patch you provided
 */
export const patchGeolocationForWebView = () => {
  if (!isWebview()) {
    return;
  }

  if (typeof navigator === "undefined" || !navigator.geolocation) {
    console.warn("[Bluedot-WebView] navigator.geolocation not available");
    return;
  }

  // Store original functions
  const originalGetCurrentPosition = navigator.geolocation.getCurrentPosition;
  const originalWatchPosition = navigator.geolocation.watchPosition;

  // Patch getCurrentPosition
  navigator.geolocation.getCurrentPosition = function(
    successCallback,
    errorCallback,
    options
  ) {
    // Check if Bluedot needs location
    if (
      bluedotConfig.enabled &&
      !locationEnabled &&
      !locationPermissionGranted
    ) {
      // Request permission first
      requestNativeLocationPermission().then(result => {
        if (result.granted && result.position) {
          successCallback(result.position);
        } else if (errorCallback) {
          errorCallback({
            code: 1,
            message: result.error || "Location permission denied"
          });
        }
      });
      return;
    }

    // Use native geolocation
    const requestId = `geolocate_${Date.now()}`;

    window.onNativeGeolocationSuccess = position => {
      try {
        const parsed =
          typeof position === "string" ? JSON.parse(position) : position;
        successCallback({
          coords: {
            latitude: parsed.latitude,
            longitude: parsed.longitude,
            accuracy: parsed.accuracy || 0,
            altitude: parsed.altitude || null,
            altitudeAccuracy: parsed.altitudeAccuracy || null,
            heading: parsed.heading || null,
            speed: parsed.speed || null
          },
          timestamp: parsed.timestamp || Date.now()
        });
      } catch (error) {
        if (errorCallback) {
          errorCallback({ code: 2, message: "Failed to parse position" });
        }
      }
      delete window.onNativeGeolocationSuccess;
    };

    window.onNativeGeolocationError = error => {
      try {
        const parsed = typeof error === "string" ? JSON.parse(error) : error;
        if (errorCallback) {
          errorCallback({
            code: parsed.code || 1,
            message: parsed.message || "Geolocation error"
          });
        }
      } catch (e) {
        if (errorCallback) {
          errorCallback({ code: 1, message: "Geolocation error" });
        }
      }
      delete window.onNativeGeolocationError;
    };

    window.postMessage(
      JSON.stringify({
        type: "GET_CURRENT_LOCATION",
        requestId,
        options: options || {}
      }),
      "*"
    );

    // Timeout fallback
    setTimeout(() => {
      if (window.onNativeGeolocationSuccess) {
        delete window.onNativeGeolocationSuccess;
        delete window.onNativeGeolocationError;
        if (errorCallback) {
          errorCallback({ code: 3, message: "Geolocation timeout" });
        }
      }
    }, 15000);
  };

  // Patch watchPosition (simplified - returns watch ID)
  navigator.geolocation.watchPosition = function(
    successCallback,
    errorCallback,
    options
  ) {
    const watchId = Date.now();

    // Set up handlers
    window.onNativeWatchPosition[watchId] = {
      success: successCallback,
      error: errorCallback
    };

    // Start watching
    window.postMessage(
      JSON.stringify({
        type: "WATCH_LOCATION",
        watchId,
        options: options || {}
      }),
      "*"
    );

    return watchId;
  };

  // Clear watch
  const originalClearWatch = navigator.geolocation.clearWatch;
  navigator.geolocation.clearWatch = function(watchId) {
    delete window.onNativeWatchPosition[watchId];
    window.postMessage(
      JSON.stringify({
        type: "CLEAR_WATCH",
        watchId
      }),
      "*"
    );
  };

  console.log(
    "[Bluedot-WebView] navigator.geolocation patched for native integration"
  );
};

/**
 * Initialize Bluedot with permission-aware logic
 * If permissions already granted, init immediately without prompt
 *
 * Uses a mutex (initializationPromise) to prevent concurrent calls from
 * racing to bluedot.initialize() and causing "already initialized" errors.
 *
 * @returns {Promise<{success: boolean, error?: string, permissionStatus: string}>}
 */
export const initializeBluedotWithPermissionCheck = async () => {
  if (!bluedotConfig.enabled) {
    return {
      success: false,
      error: "Bluedot disabled",
      permissionStatus: "disabled"
    };
  }

  if (isInitialized) {
    return { success: true, permissionStatus: nativeLocationPermission };
  }

  // Prevent concurrent initialization calls from racing
  if (initializationPromise) {
    return initializationPromise;
  }

  initializationPromise = _doInitialize();
  try {
    return await initializationPromise;
  } finally {
    initializationPromise = null;
  }
};

const _doInitialize = async () => {
  const isWebViewMode = isWebview();
  const environment = isWebViewMode ? "webview" : "standalone";

  console.log(`[Bluedot-WebView] Initializing for ${environment}...`);

  try {
    // Step 1: Check permissions BEFORE initializing
    const hasPermission = await checkNativeLocationPermission();

    if (hasPermission) {
      console.log(
        "[Bluedot-WebView] Location permission available:",
        nativeLocationPermission
      );
      locationPermissionGranted = true;
    } else {
      console.log(
        "[Bluedot-WebView] No location permission:",
        nativeLocationPermission
      );
    }

    // Step 2: Patch geolocation for WebView
    if (isWebViewMode) {
      patchGeolocationForWebView();
    }

    // Step 3: Initialize Bluedot SDK
    await bluedot.initialize(bluedotConfig.projectId);
    console.log("[Bluedot-WebView] SDK initialized");

    // Step 4: Configure based on permission status
    if (bluedotConfig.debugMode) {
      bluedot.config.setDebugModeEnabled(true);
    }

    if (bluedotConfig.mockMode === false) {
      try {
        bluedot.config.setUserPersistenceEnabled(true);
      } catch (e) {
        // Ignore
      }
    }

    // Step 5: If we have permission, enable location tracking
    if (locationPermissionGranted) {
      try {
        await bluedot.setLocationEnabled(true);
        locationEnabled = true;
        console.log("[Bluedot-WebView] Location enabled (had permission)");
      } catch (e) {
        console.warn("[Bluedot-WebView] Failed to enable location:", e.message);
      }
    }

    isInitialized = true;
    initializationError = null;

    return {
      success: true,
      environment,
      permissionStatus: nativeLocationPermission
    };
  } catch (error) {
    const errorMsg = error.message || "";

    // SDK was already initialized (e.g. by a concurrent call or previous
    // session). Treat as success rather than a fatal error.
    if (errorMsg.includes("already initialized")) {
      console.log(
        "[Bluedot-WebView] SDK already initialized externally, syncing state"
      );
      isInitialized = true;
      initializationError = null;

      return {
        success: true,
        environment,
        permissionStatus: nativeLocationPermission
      };
    }

    console.error("[Bluedot-WebView] Initialization failed:", errorMsg);
    initializationError = error;
    isInitialized = false;

    return {
      success: false,
      error: errorMsg,
      permissionStatus: nativeLocationPermission
    };
  }
};

/**
 * Get current Bluedot integration status
 */
export const getBluedotIntegrationStatus = () => ({
  enabled: bluedotConfig.enabled,
  initialized: isInitialized,
  error: initializationError,
  locationEnabled,
  locationPermissionGranted,
  nativePermissionStatus: nativeLocationPermission,
  environment: isWebview() ? "webview" : "standalone",
  debugMode: bluedotConfig.debugMode
});

/**
 * Reset integration state (for logout/testing)
 */
export const resetBluedotIntegration = () => {
  try {
    bluedot.reset();
  } catch (e) {
    // Ignore
  }

  isInitialized = false;
  initializationError = null;
  initializationPromise = null;
  locationEnabled = false;
  locationPermissionGranted = false;
  nativePermissionChecked = false;
  nativeLocationPermission = "unknown";

  console.log("[Bluedot-WebView] Integration reset");
};

export default {
  checkBrowserPermission,
  checkNativeLocationPermission,
  requestNativeLocationPermission,
  patchGeolocationForWebView,
  initializeBluedotWithPermissionCheck,
  getBluedotIntegrationStatus,
  resetBluedotIntegration
};
